import { business } from "@/config/business";
import { blogPosts } from "@/config/blog-posts";
import { ppcCampaigns } from "@/config/campaigns";
import { beforeAfterProjects } from "@/config/projects";
import { confirmedServiceAreas } from "@/config/service-areas";
import { publishedServicePages } from "@/config/services";
import { DEFAULT_LOCALE, LOCALES, localeHrefLang, type LocaleCode } from "@/i18n/locale-config";
import {
  localizedPath,
  resolveRouteFromPath,
  routeIdToEnglishCanonicalPath,
  type RouteId,
} from "@/i18n/route-manifest";
import { localizedPageMeta } from "@/i18n/page-meta-translations";

export type SeoLocale = {
  code: LocaleCode;
  hrefLang: string;
  openGraphLocale: string;
};

export type HreflangLink = {
  hrefLang: string;
  href: string;
};

export type RobotsDirective = "index, follow" | "noindex, nofollow";

export type ResolvedPageMetadata = {
  routeId: RouteId;
  canonicalPath: string;
  localizedPath: string;
  locale: LocaleCode;
  title: string;
  description: string;
  canonicalUrl: string;
  hreflang: HreflangLink[];
  openGraph: {
    type: "website";
    siteName: string;
    locale: string;
    alternateLocales: string[];
    title: string;
    description: string;
    url: string;
    image: string;
    imageAlt: string;
    imageType: "image/jpeg" | "image/png";
  };
  robots: RobotsDirective;
};

type PageDefinition = {
  routeId: RouteId;
  title: string;
  description: string;
  kind: "home" | "core" | "service" | "service-area-index" | "service-area" | "utility";
  robots?: RobotsDirective;
  ogImage?: string;
  ogImageAlt?: string;
};

export const SEO_LOCALES: readonly SeoLocale[] = LOCALES.map((locale) => ({
  code: locale.routeLocaleKey,
  hrefLang: locale.hrefLang,
  openGraphLocale: locale.bcp47.replace("-", "_"),
}));

const CORE_PAGE_DEFINITIONS: readonly PageDefinition[] = [
  {
    routeId: "home",
    title: "Revolin Roofing | Premium Roofing in Metro Vancouver, BC",
    description:
      "Roof replacement, repair and premium roofing systems for Metro Vancouver homeowners, with detailed assessment and a transparent written scope.",
    kind: "home",
  },
  {
    routeId: "core:services",
    title: "Roofing Services in Metro Vancouver | Revolin Roofing",
    description:
      "Explore roof replacement, repair, asphalt shingle, metal, low-slope and inspection services for Metro Vancouver homes.",
    kind: "core",
  },
  {
    routeId: "core:about",
    title: "About Revolin Roofing | A Revolin Group Company",
    description:
      "Learn how Revolin Roofing approaches assessments, written scopes, project coordination and roofing decisions for Metro Vancouver homes.",
    kind: "core",
  },
  {
    routeId: "core:before-after",
    title: "Before & After Roofing Gallery | Revolin Roofing",
    description:
      "Explore Revolin Roofing before-and-after galleries across Metro Vancouver: leak repair, asphalt, metal, cedar, and flat-roof transformations for Lower Mainland homeowners.",
    kind: "core",
  },
  {
    routeId: "core:service-areas",
    title: "Metro Vancouver Roofing Service Areas | Revolin Roofing",
    description:
      "View confirmed Revolin Roofing service areas across Metro Vancouver and the Lower Mainland. These are service areas, not branch offices.",
    kind: "service-area-index",
  },
  {
    routeId: "core:emergency-roofing",
    title: "Emergency Roofing in the Lower Mainland | Revolin Roofing",
    description:
      "Get urgent help for active roof leaks and storm damage across the Lower Mainland, subject to crew availability and safe site access.",
    kind: "service",
    ogImage: "/media/expansion-pages/emergency-roofing-lower-mainland.jpg",
    ogImageAlt: "Emergency roof assessment on a Lower Mainland home",
  },
  {
    routeId: "core:commercial-roofing",
    title: "Commercial Roofing in British Columbia | Revolin Roofing",
    description:
      "Assessment-led commercial roof repair, replacement and maintenance for planned projects across British Columbia, subject to scope and mobilization.",
    kind: "service",
    ogImage: "/media/expansion-pages/commercial-roofing-bc.jpg",
    ogImageAlt: "Commercial low-slope roofing in British Columbia",
  },
  {
    routeId: "core:strata-roofing",
    title: "Strata Roofing in British Columbia | Revolin Roofing",
    description:
      "Roof assessments, repairs, replacement planning and preventive maintenance for strata communities and property managers across British Columbia.",
    kind: "service",
    ogImage: "/media/expansion-pages/strata-roofing-bc.jpg",
    ogImageAlt: "Strata roofing project in British Columbia",
  },
  {
    routeId: "core:residential-roofing",
    title: "Residential Roofing in British Columbia | Revolin Roofing",
    description:
      "Residential roof repair and replacement in the Lower Mainland, plus qualifying scheduled roofing projects throughout British Columbia.",
    kind: "service",
    ogImage: "/media/expansion-pages/residential-roofing-bc.jpg",
    ogImageAlt: "Residential roofing on a British Columbia home",
  },
  {
    routeId: "core:roof-systems",
    title: "Roofing Systems for BC Buildings | Revolin Roofing",
    description:
      "Compare asphalt shingles, cedar, metal and low-slope membrane systems with guidance based on building needs, exposure, drainage and project scope.",
    kind: "service",
    ogImage: "/media/expansion-pages/roof-systems-bc.jpg",
    ogImageAlt: "Premium roofing systems suited to British Columbia buildings",
  },
  {
    routeId: "core:roof-estimate",
    title: "Request a Roofing Estimate in BC | Revolin Roofing",
    description:
      "Share your property, roof, timing and project details so Revolin Roofing can review the scope and prepare a preliminary roofing estimate.",
    kind: "core",
    ogImage: "/media/expansion-pages/roof-estimate-bc.jpg",
    ogImageAlt: "Roofing specialist reviewing a British Columbia project",
  },
  {
    routeId: "core:british-columbia",
    title: "Roofing Services Across British Columbia | Revolin Roofing",
    description:
      "Emergency roofing serves the Lower Mainland, while qualifying scheduled residential, commercial and strata projects are available across British Columbia.",
    kind: "service-area-index",
    ogImage: "/media/expansion-pages/british-columbia-roofing.jpg",
    ogImageAlt: "Scheduled roofing project coverage across British Columbia",
  },
  {
    routeId: "core:blog",
    title: "Roofing Guides & Articles | Revolin Roofing",
    description:
      "Educational roofing guides for Metro Vancouver homeowners: repair signs, material comparisons, and diagnosing ceiling stains.",
    kind: "core",
  },
  {
    routeId: "core:faq",
    title: "Roofing Questions & Answers | Revolin Roofing",
    description:
      "Clear answers about roof assessment, repair, replacement, roofing systems, project scope and Metro Vancouver conditions.",
    kind: "core",
  },
  {
    routeId: "core:contact",
    title: "Contact Revolin Roofing | Request a Roof Assessment",
    description:
      "Contact Revolin Roofing to discuss a roof assessment, repair, replacement or roofing-system question in the confirmed service area.",
    kind: "core",
  },
  {
    routeId: "core:privacy",
    title: "Privacy Policy | Revolin Roofing",
    description:
      "Read how Revolin Roofing handles website enquiries, attribution information and personal information submitted through this site.",
    kind: "core",
  },
  {
    routeId: "core:terms",
    title: "Website Terms | Revolin Roofing",
    description:
      "Read the terms governing use of the Revolin Roofing website and its general educational roofing content.",
    kind: "core",
  },
  {
    routeId: "core:thank-you",
    title: "Thank You | Revolin Roofing",
    description: "Thank you for contacting Revolin Roofing. We will review your enquiry.",
    kind: "utility",
    robots: "noindex, nofollow",
  },
] as const;

const SERVICE_PAGE_DEFINITIONS: readonly PageDefinition[] = publishedServicePages.map(
  (service) => ({
    routeId: `service:${service.slug}` as RouteId,
    title: service.metaTitle,
    description: service.metaDescription,
    kind: "service" as const,
  }),
);

const SERVICE_AREA_PAGE_DEFINITIONS: readonly PageDefinition[] = confirmedServiceAreas.map(
  (area) => ({
    routeId: `area:${area.slug}` as RouteId,
    title: area.metaTitle,
    description: area.metaDescription,
    kind: "service-area" as const,
  }),
);

const PROJECT_PAGE_DEFINITIONS: readonly PageDefinition[] = beforeAfterProjects.map((project) => ({
  routeId: `project:${project.slug}` as RouteId,
  title: `${project.title} | Revolin Roofing`,
  description: project.solution ?? project.shortProblem ?? project.title,
  kind: "core" as const,
}));

const BLOG_PAGE_DEFINITIONS: readonly PageDefinition[] = blogPosts.map((post) => ({
  routeId: `blog:${post.slug}` as RouteId,
  title: post.metaTitle,
  description: post.metaDescription,
  kind: "core" as const,
  ogImage: post.heroImage,
  ogImageAlt: post.heroAlt,
}));

const CAMPAIGN_PAGE_DEFINITIONS: readonly PageDefinition[] = ppcCampaigns
  .filter((campaign) => campaign.indexable)
  .map((campaign) => ({
    routeId: `campaign:${campaign.slug}` as RouteId,
    title: campaign.metaTitle,
    description: campaign.metaDescription,
    kind: "core" as const,
  }));

export const PAGE_DEFINITIONS: readonly PageDefinition[] = [
  ...CORE_PAGE_DEFINITIONS,
  ...SERVICE_PAGE_DEFINITIONS,
  ...SERVICE_AREA_PAGE_DEFINITIONS,
  ...PROJECT_PAGE_DEFINITIONS,
  ...BLOG_PAGE_DEFINITIONS,
  ...CAMPAIGN_PAGE_DEFINITIONS,
];

export const INDEXABLE_ROUTE_IDS: readonly RouteId[] = PAGE_DEFINITIONS.filter(
  (page) => (page.robots ?? "index, follow") === "index, follow",
).map((page) => page.routeId);

const PAGE_BY_ROUTE_ID = new Map(PAGE_DEFINITIONS.map((page) => [page.routeId, page]));

export const SITE_ORIGIN = normalizeOrigin(import.meta.env.VITE_SITE_URL || business.url);

function normalizeOrigin(origin: string): string {
  return origin.trim().replace(/\/+$/, "");
}

export function absoluteLocalizedUrl(
  routeId: RouteId,
  locale: LocaleCode,
  origin = SITE_ORIGIN,
): string {
  return `${normalizeOrigin(origin)}${localizedPath(routeId, locale)}`;
}

export function seoLocalesForRoute(routeId: RouteId): readonly SeoLocale[] {
  const definition = PAGE_BY_ROUTE_ID.get(routeId);
  if (definition?.kind === "utility") {
    return SEO_LOCALES.filter((locale) => locale.code === DEFAULT_LOCALE);
  }
  return SEO_LOCALES;
}

export function hreflangLinksForRoute(routeId: RouteId, origin = SITE_ORIGIN): HreflangLink[] {
  const links = seoLocalesForRoute(routeId).map((locale) => ({
    hrefLang: locale.hrefLang,
    href: absoluteLocalizedUrl(routeId, locale.code, origin),
  }));

  links.push({
    hrefLang: "x-default",
    href: absoluteLocalizedUrl(routeId, DEFAULT_LOCALE, origin),
  });
  return links;
}

export function resolvePageMetadata(
  pathname: string,
  origin = SITE_ORIGIN,
): ResolvedPageMetadata | undefined {
  const parsed = resolveRouteFromPath(pathname);
  if (!parsed?.route) return undefined;

  const definition = PAGE_BY_ROUTE_ID.get(parsed.route.routeId);
  if (!definition) return undefined;

  if (!seoLocalesForRoute(definition.routeId).some((entry) => entry.code === parsed.locale)) {
    return undefined;
  }

  const canonicalPath = routeIdToEnglishCanonicalPath(definition.routeId);
  const localizedPublicPath = localizedPath(definition.routeId, parsed.locale);
  const canonicalUrl = absoluteLocalizedUrl(definition.routeId, parsed.locale, origin);
  const copy = localizedPageMeta(canonicalPath, parsed.locale, {
    title: definition.title,
    description: definition.description,
  });

  const localeDef = LOCALES.find((l) => l.routeLocaleKey === parsed.locale)!;
  const openGraphImage = `${normalizeOrigin(origin)}${definition.ogImage ?? "/RevolinGroup-new-logo.png"}`;
  const openGraphImageAlt = definition.ogImageAlt ?? `${business.name} logo`;
  const openGraphImageType = definition.ogImage ? "image/jpeg" : "image/png";

  return {
    routeId: definition.routeId,
    canonicalPath,
    localizedPath: localizedPublicPath,
    locale: parsed.locale,
    title: copy.title,
    description: copy.description,
    canonicalUrl,
    hreflang: hreflangLinksForRoute(definition.routeId, origin),
    openGraph: {
      type: "website",
      siteName: business.name,
      locale: localeDef.bcp47.replace("-", "_"),
      alternateLocales: seoLocalesForRoute(definition.routeId)
        .filter((entry) => entry.code !== parsed.locale)
        .map((entry) => entry.openGraphLocale),
      title: copy.title,
      description: copy.description,
      url: canonicalUrl,
      image: openGraphImage,
      imageAlt: openGraphImageAlt,
      imageType: openGraphImageType,
    },
    robots: definition.robots ?? "index, follow",
  };
}

export function metadataToRouteHead(metadata: ResolvedPageMetadata) {
  return {
    meta: [
      { title: metadata.title },
      { name: "description", content: metadata.description },
      { name: "robots", content: metadata.robots },
      { property: "og:type", content: metadata.openGraph.type },
      { property: "og:site_name", content: metadata.openGraph.siteName },
      { property: "og:locale", content: metadata.openGraph.locale },
      ...metadata.openGraph.alternateLocales.map((locale) => ({
        property: "og:locale:alternate",
        content: locale,
      })),
      { property: "og:title", content: metadata.openGraph.title },
      { property: "og:description", content: metadata.openGraph.description },
      { property: "og:url", content: metadata.openGraph.url },
      { property: "og:image", content: metadata.openGraph.image },
      { property: "og:image:alt", content: metadata.openGraph.imageAlt },
      { property: "og:image:type", content: metadata.openGraph.imageType },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: metadata.title },
      { name: "twitter:description", content: metadata.description },
      { name: "twitter:image", content: metadata.openGraph.image },
    ],
    links: [
      { rel: "canonical", href: metadata.canonicalUrl },
      ...metadata.hreflang.map((link) => ({
        rel: "alternate",
        hrefLang: link.hrefLang,
        href: link.href,
      })),
    ],
  };
}

/** @deprecated Use INDEXABLE_ROUTE_IDS */
export const INDEXABLE_CANONICAL_PATHS: readonly string[] = INDEXABLE_ROUTE_IDS.map(
  routeIdToEnglishCanonicalPath,
);

export { localeHrefLang };
